/**
 * Numéros de passage — règles pures (spécification `numeros-passage.md`,
 * R1 à R5 ; décisions 278, 280, 282).
 *
 * Un numéro = lettre de spécialité + lettre de lien + rang : `GA1`, `GC12`,
 * `DX3`. Le rang vient d'un compteur en base (un par préfixe, centre et jour
 * à Madagascar) ; tout le reste se décide ici, pour l'API et l'écran de
 * Configuration à la fois.
 */
/** La clé du paramètre de Configuration qui porte la table prestation → lettre (R4). */
export const CLE_LETTRES_PRESTATIONS = 'parcours_patient.lettres_prestations';
/** Les valeurs par défaut de la spécification R4 (décision 282), à faire valider par la Direction. */
export const LETTRES_PRESTATIONS_PAR_DEFAUT = 'GÉNÉRALISTE=G; DENTISTE=D; GYNÉCOLOGIE=Y; ÉCHOGRAPHIE=H; VISITE SYSTÉMATIQUE=V; ' +
    "VISITE D'EMBAUCHE=B; MÉDECINE DU TRAVAIL=M; CARDIOLOGIE=K; ENDOCRINOLOGIE=N; CPN=P; " +
    'KINÉSITHÉRAPIE=T; SOINS=S';
/** R4 — lettres de lien, et I, O, Q qui se confondent avec 1 et 0 : jamais une spécialité. */
export const LETTRES_INTERDITES_SPECIALITE = ['A', 'C', 'E', 'X', 'I', 'O', 'Q'];
/** Les 19 lettres autorisées, dans l'ordre alphabétique. */
const AUTORISEES = 'BDFGHJKLMNPRSTUVWYZ'.split('');
/**
 * Décision 301 — un code de spécialité : une lettre autorisée, ou deux quand
 * les 19 sont prises. Ni l'une ni l'autre n'est une lettre de lien ni I, O, Q :
 * « GYA1 » se lit sans ambiguïté GY + A, puisque A ne peut pas être la 2e lettre.
 */
function codeAutorise(code) {
    return (/^[A-Z]{1,2}$/.test(code) &&
        AUTORISEES.includes(code.charAt(0)) &&
        (code.length === 1 || AUTORISEES.includes(code.charAt(1))));
}
/** La forme d'une prestation qui sert à comparer : sans casse, sans accent, espaces resserrés. */
function clePrestation(prestation) {
    return prestation
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[’‘`]/g, "'")
        .trim()
        .replace(/\s+/g, ' ')
        .toUpperCase();
}
/**
 * Lit la table saisie en Configuration (`PRESTATION=L; …`, `;` ou retour à
 * la ligne entre deux paires) : la table, ou le premier défaut en français.
 */
export function lireTableDesLettres(texte) {
    const refus = (erreur) => ({ table: null, erreur });
    const table = new Map();
    const paires = texte.split(/[;\n]/).filter((p) => p.trim() !== '');
    if (paires.length === 0)
        return refus('Au moins une prestation est attendue.');
    for (const paire of paires.map((p) => p.trim())) {
        const [nom = '', lettreSaisie = '', ...reste] = paire.split('=');
        const prestation = clePrestation(nom);
        const lettre = lettreSaisie.trim().toUpperCase();
        const deja = [...table].find(([, l]) => l === lettre)?.[0];
        if (reste.length > 0 || prestation === '' || lettreSaisie === '') {
            return refus(`« ${paire} » : attendu PRESTATION=LETTRE.`);
        }
        if (!/^[A-Z]{1,2}$/.test(lettre)) {
            return refus(`« ${paire} » : une ou deux lettres de A à Z.`);
        }
        if (!codeAutorise(lettre)) {
            return refus(`« ${paire} » : ${LETTRES_INTERDITES_SPECIALITE.join(', ')} ne sont pas des lettres de spécialité (lien avec l’assuré, ou confusion avec 1 et 0).`);
        }
        if (table.has(prestation))
            return refus(`${prestation} figure deux fois.`);
        if (deja !== undefined)
            return refus(`Le code ${lettre} est déjà celui de ${deja}.`);
        table.set(prestation, lettre);
    }
    return { table, erreur: null };
}
const TABLE_PAR_DEFAUT = lireTableDesLettres(LETTRES_PRESTATIONS_PAR_DEFAUT).table ?? new Map();
/** La table enregistrée, ou celle par défaut si la valeur en base ne se lit pas. */
export function tableDesLettresOuDefaut(texte) {
    return lireTableDesLettres(texte).table ?? TABLE_PAR_DEFAUT;
}
/** `null` si la valeur du paramètre est une table valable, sinon le message. */
export function validerTableDesLettres(texte) {
    return lireTableDesLettres(texte).erreur;
}
/** R4 — la lettre que la table donne à la prestation (sans casse ni accent), s'il y en a une. */
export function lettreDeLaTable(prestation, table) {
    return table.get(clePrestation(prestation));
}
/**
 * R5 (décisions 291, 301) — le code à donner à une prestation nouvelle : sa
 * première lettre si elle est autorisée et libre, sinon la première lettre
 * autorisée et libre ; les 19 prises, un code de deux lettres libre (sa
 * première lettre d'abord, s'il se peut). `null` seulement si les 380 codes
 * sont pris.
 */
export function lettreLibre(prestation, table) {
    const prises = new Set(table.values());
    const premiere = clePrestation(prestation).charAt(0);
    const tetes = AUTORISEES.includes(premiere)
        ? [premiere, ...AUTORISEES.filter((l) => l !== premiere)]
        : AUTORISEES;
    const candidats = [...tetes, ...tetes.flatMap((a) => AUTORISEES.map((b) => `${a}${b}`))];
    return candidats.find((code) => !prises.has(code)) ?? null;
}
/**
 * Décision 291 — la table avec une paire de plus, telle qu'elle s'enregistre.
 * `null` si le nom ne peut pas y figurer tel quel (`;`, `=` ou retour à la ligne).
 */
export function tableAvecPaire(texte, prestation, lettre) {
    if (/[;=\n]/.test(prestation) || clePrestation(prestation) === '')
        return null;
    const base = texte.trim().replace(/;$/, '');
    return `${base === '' ? '' : `${base}; `}${prestation.trim()}=${lettre}`;
}
/** R1 — le numéro complet, sans espace ni zéro en tête. */
export function numeroDePassage(prefixe, rang) {
    return `${prefixe}${String(rang)}`;
}
//# sourceMappingURL=numero-passage.js.map